import { useState, FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AlertCircle, CheckCircle } from 'lucide-react'
import { useAuth } from '@wrenchlab-ui/hooks/useAuth'
import { Input } from '@wrenchlab-ui/components/ui/Input'
import { Button } from '@wrenchlab-ui/components/ui/Button'

export default function ResetPasswordPage() {
  const { resetPassword } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!token) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="flex justify-center mb-8">
            <img src="/logo.png" alt="WrenchLab" className="h-24 w-auto object-contain" />
          </div>
          <div className="wl-card p-6 text-center">
            <p className="text-sm text-neutral-600 mb-4">
              This reset link is invalid or missing. Please request a new one.
            </p>
            <button
              onClick={() => navigate('/forgot-password')}
              className="text-sm text-primary-600 hover:underline"
            >
              Request password reset
            </button>
          </div>
        </div>
      </div>
    )
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    setLoading(true)
    try {
      await resetPassword(token, newPassword)
      setSuccess(true)
      setTimeout(() => navigate('/login'), 2000)
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string; message?: string } } }
      const data = ax?.response?.data
      if (data?.error === 'INVALID_TOKEN') {
        setError(data?.message ?? 'This reset link is invalid or has expired. Please request a new one.')
      } else if (data?.error === 'WEAK_PASSWORD') {
        setError(data?.message ?? 'Password must be at least 8 characters.')
      } else {
        setError('Something went wrong. Please try again or request a new reset link.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-8">
          <img src="/logo.png" alt="WrenchLab" className="h-24 w-auto object-contain" />
        </div>

        <div className="wl-card p-6">
          <h1 className="text-lg font-semibold text-neutral-900 mb-1">Set a new password</h1>
          <p className="text-sm text-neutral-500 mb-5">Choose a strong password for your workspace.</p>

          {success ? (
            <div className="flex gap-3 rounded-wl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
              <CheckCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <p>Password reset successfully! Redirecting to sign in…</p>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 flex gap-3 rounded-wl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <p>{error}</p>
                    {error.includes('expired') && (
                      <button
                        type="button"
                        onClick={() => navigate('/forgot-password')}
                        className="mt-1 inline-block underline font-medium"
                      >
                        Request a new link →
                      </button>
                    )}
                  </div>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  label="New Password"
                  type="password"
                  placeholder="At least 8 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoFocus
                  autoComplete="new-password"
                  hint="Minimum 8 characters"
                />
                <Input
                  label="Confirm Password"
                  type="password"
                  placeholder="Re-enter password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                />
                <Button type="submit" variant="primary" className="w-full" loading={loading}>
                  Reset Password
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
